<template>
	<div class="ventas_tab report_theme_container" data-theme="ventas">
		<TabHeader
			title="Ventas"
			description="Analiza la tendencia y composición de tus ventas"
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth">
			<template #center-sub-nav>
				<div class="sub_nav_filters">
					<button v-for="filter in subNavFilters" :key="filter.id" class="filter_btn"
						:class="{ 'is-active': activeSubFilter === filter.id }"
						@click="updateActiveSubFilter(filter.id)">
						{{ filter.label }}
					</button>
				</div>
			</template>
		</TabHeader>

		<!-- Summary Cards Section: colapsable para liberar alto vertical cuando
			el foco está en la tabla. Estado persiste en localStorage por tab. -->
		<CollapsibleSection class="mt-1"
			title="Resumen"
			storageKey="financialReports:summary-expanded">
			<template v-if="summaryHint" #hint>{{ summaryHint }}</template>
			<SalesSummaryCards
				:monthHeaders="monthHeaders"
				:tableRows="tableRows"
				:totalRow="totalRow" />
		</CollapsibleSection>

		<!-- Main Table Section -->
		<SalesEvolutionTable class="mt-2"
			:monthHeaders="monthHeaders"
			:tableRows="visibleRows"
			:totalRow="totalRow"
			:expandedL2Rows="expandedL2Rows"
			:isLoading="isLoading"
			:error="error"
			:selectedPeriod="selectedPeriodRange"
			:periodOptions="periodOptions"
			:filterPeriod="filterPeriod"
			@change-period="setPeriodRange"
			@retry="fetchSalesData"
			@cell-click="openSalesAnalysis"
			@toggle-l2="toggleL2" />
	</div>
</template>

<script>
import moment from "moment";
import "moment/locale/es";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import CollapsibleSection from "@/components/financialReports/CollapsibleSection.vue";
import SalesSummaryCards from "@/components/financialReports/SalesSummaryCards.vue";
import SalesEvolutionTable from "@/components/financialReports/SalesEvolutionTable.vue";
import ModalFullHeightRight from "@/components/modals/ModalFullHeightRight.vue";
import HeaderSalesAnalysis from "@/components/financialReports/salesAnalysis/HeaderSalesAnalysis.vue";
import BodySalesAnalysis from "@/components/financialReports/salesAnalysis/BodySalesAnalysis.vue";
import { PERIOD_TYPES, FILTER_PERIOD_OPTIONS } from "../config/reportStructure";
import { capitalize } from "@/utils/string";
import { parseToMoney } from "@/utils/currency";

export default {
	name: "VentasTab",

	components: {
		TabHeader,
		CollapsibleSection,
		SalesSummaryCards,
		SalesEvolutionTable,
	},

	data() {
		return {
			periodOptions: PERIOD_TYPES,
			filterOptions: FILTER_PERIOD_OPTIONS,
			// Cuentas L2 expandidas en el modo jerárquico (sub-filter
			// "clientes"). Resetea a [] al cambiar de sub-filter para que
			// labels de otro modo no queden colgados visualmente.
			expandedL2Rows: [],
			// ID del setTimeout del debounce de category-change. Inicializado
			// en data() (en lugar de como propiedad `this._xxx` ad-hoc) para
			// que sea forward-compat con Vue 3 — el Proxy intercepta toda
			// asignación a `this`, así que el truco del prefijo "_" para
			// evadir el observer de Vue 2 deja de aplicar. Hacerlo reactive
			// es inofensivo (es un número, sin template binding).
			categoryChangeTimer: null,
		};
	},

	computed: {
		...mapState("classification", ["categories", "itemsByCategory"]),
		...mapState("reportsFilters", ["endMonth", "selectedPeriodRange"]),
		...mapState("financialReports", ["filterPeriod"]),
		...mapState("salesReport", [
			"activeSubFilter",
			"monthHeaders",
			"isLoading",
			"error",
			"summaryCards",
		]),
		// Lente activo del drawer — el header del modal puede cambiar el mes y
		// necesitamos retener account/groupBy/selectedItems para reusarlos en
		// el refetch sin leer state.x directo desde un handler.
		...mapState("salesAnalysis", {
			analysisAccount: "account",
			analysisGroupBy: "groupBy",
			analysisSelectedItems: "selectedItems",
			analysisMonth: "month",
		}),
		...mapGetters("salesReport", ["tableRows", "totalRow", "isHierarchical"]),

		// Modo jerárquico: oculta filas L3 cuya cuenta padre no esté expandida.
		// L2 siempre visible — actúa como "header" colapsable de su grupo.
		// Modo plano: pass-through (sin levels, no hay filtrado por hacer).
		visibleRows() {
			if (!this.isHierarchical) return this.tableRows;
			return this.tableRows.filter((row) => {
				if (row.level === 2) return true;
				if (row.level === 3) return this.expandedL2Rows.includes(row.parent);
				return true;
			});
		},

		getNow() {
			return moment().toDate();
		},

		// Hint compacto que se muestra al lado del label "Resumen" cuando la
		// sección está colapsada — preserva la señal crítica (monto del mes
		// actual) para que el modo foco no pierda contexto.
		summaryHint() {
			const amount = this.summaryCards && this.summaryCards.current_month
				? this.summaryCards.current_month.amount
				: null;
			return amount != null ? parseToMoney(amount) : "";
		},

		subNavFilters() {
			const filters = [{ id: "cuentas", label: "Cuentas" }];
			this.categories.forEach(category => {
				filters.push({ id: category, label: capitalize(category) });
			});
			filters.push({ id: "clientes", label: "Clientes" });
			return filters;
		},

		// Categorías dinámicas del tab (tiendas, marcas, …) sin las pseudo-
		// categorías cuentas/clientes — esas ya tienen UI propia en otra parte
		// (sub-nav del tab y card del body). Cada entrada va a un
		// SelectComponent en el header del drawer. Las options vienen del
		// classification store (itemsByCategory), poblado por la action
		// `classification/getCategories` que disparamos en `mounted` (short-
		// circuit si ya está cargado).
		headerCategories() {
			return this.subNavFilters
				.filter((f) => f.id !== "cuentas" && f.id !== "clientes")
				.map((f) => ({
					id: f.id,
					label: f.label,
					options: (this.itemsByCategory[f.id] || []).map((name) => ({
						value: name,
						label: name,
					})),
				}));
		},

		// groupBy define la categoría dinámica para stores_distribution. Cuando
		// el sub-nav activo es 'cuentas' o 'clientes' (pseudo-categorías sin
		// agrupación atributo-based), caemos a la primera categoría disponible
		// del classification store — sin esto, el modal abierto desde el modo
		// 'cuentas' no tendría card de distribución por tienda/marca.
		analysisGroupByForOpen() {
			const sub = this.activeSubFilter;
			if (sub && sub !== "cuentas" && sub !== "clientes") return sub;
			return this.categories[0] || null;
		},

		// Firma reactiva consolidada — un solo trigger para fetch aunque
		// varios filtros cambien en la misma transacción.
		fetchSignature() {
			return [this.endMonth, this.selectedPeriodRange, this.activeSubFilter, this.filterPeriod].join("|");
		},

		// Las summary cards solo dependen del mes de referencia: refetch
		// independiente para no perderlas al cambiar period range o sub_filter.
		summarySignature() {
			return moment(this.endMonth).format("YYYY-MM");
		},
	},

	methods: {
		...mapActions("salesReport", ["initVentas", "fetchSalesData", "fetchSalesSummary"]),
		...mapActions("reportsFilters", ["setPeriodRange"]),
		...mapActions("salesAnalysis", ["fetchSalesAnalysis"]),
		// Categorías + items son globales (no específicos de Ventas); el
		// store hace short-circuit si ya están cargados por otro view.
		...mapActions("classification", ["getCategories"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("salesReport", ["SET_ACTIVE_SUB_FILTER"]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),
		...mapMutations("salesAnalysis", {
			resetSalesAnalysis: "RESET",
			setSelectedItems: "SET_SELECTED_ITEMS",
		}),

		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateActiveSubFilter(filter) {
			// Limpia expansiones del modo anterior — labels de cuentas L2 no
			// se conservan entre sub-filters distintos.
			this.expandedL2Rows = [];
			this.SET_ACTIVE_SUB_FILTER(filter);
		},
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
		toggleL2(label) {
			const idx = this.expandedL2Rows.indexOf(label);
			if (idx > -1) this.expandedL2Rows.splice(idx, 1);
			else this.expandedL2Rows.push(label);
		},

		// Convierte "abr 2025" (label esp. del header de la tabla) a "2025-04".
		// El parseo cae a "" si el formato no corresponde — el caller lo evita
		// al ya restringir cellsClickable a filterPeriod === "month".
		parseMonthLabel(monthLabel) {
			const parsed = moment(monthLabel, "MMM YYYY", "es");
			return parsed.isValid() ? parsed.format("YYYY-MM") : "";
		},

		// Refetch del análisis cuando el usuario cambia el mes desde el
		// AnualCalendar del header del drawer. account/groupBy/selectedItems
		// se conservan (vía mapState) — el drawer sigue contextualizado al
		// mismo lente, sólo cambia el período.
		onAnalysisMonthChange(date) {
			const month = moment(date).format("YYYY-MM");
			this.fetchSalesAnalysis({
				month,
				account: this.analysisAccount,
				groupBy: this.analysisGroupBy,
				selectedItems: this.analysisSelectedItems,
			});
		},

		// Cambio de filtro multi-select en un SelectComponent del header.
		// 1) Merge inmediato al store para que el header refleje el estado
		//    sin esperar al fetch.
		// 2) Debounce del refetch — varios checkbox toggles seguidos quedan
		//    colapsados en un solo request.
		onCategoryChange({ id, values }) {
			const merged = { ...this.analysisSelectedItems, [id]: values };
			this.setSelectedItems(merged);
			clearTimeout(this.categoryChangeTimer);
			this.categoryChangeTimer = setTimeout(() => {
				this.fetchSalesAnalysis({
					month: this.analysisMonth,
					account: this.analysisAccount,
					groupBy: this.analysisGroupBy,
					selectedItems: merged,
				});
			}, 600);
		},

		// Wrap del cleanup del store + cancelación del timer pendiente —
		// si el user cierra el drawer mientras hay un fetch debounced, evita
		// dispatchear contra un store ya reseteado.
		onAnalysisClose() {
			clearTimeout(this.categoryChangeTimer);
			this.categoryChangeTimer = null;
			this.resetSalesAnalysis();
		},

		openSalesAnalysis({ monthLabel, account, level, parent }) {
			const month = this.parseMonthLabel(monthLabel);
			if (!month) return;

			// El backend de analysis filtra por cuenta L3 (`level_3_name`) y
			// por atributo (`attribute_filters`). Mapeamos el click según el
			// modo del sub-nav del tab:
			//
			// - Sub-nav "cuentas":   row.label = nombre de cuenta L3 → drillAccount.
			// - Sub-nav "clientes":  L2 row = cuenta padre, L3 row = cliente con
			//                        cuenta en `parent`. En ambos `drillAccount`
			//                        apunta a la cuenta (el backend no filtra por
			//                        cliente todavía).
			// - Sub-nav categoría:   row.label = valor del atributo (tienda,
			//                        marca, etc.) → va a `selectedItems` para
			//                        prefiltrar el drawer + preseleccionar el
			//                        SelectComponent de esa categoría en el header.
			//
			// La fila "Ventas Netas" emite account=null — semánticamente "todas
			// las cuentas / categorías / clientes". El drawer se abre sin filtro
			// específico en ningún modo: ni level_3_name ni attribute_filters
			// recortan; el agregado del mes es el que se ve.
			const sub = this.activeSubFilter;
			const isDynamicCategory = sub && sub !== "cuentas" && sub !== "clientes";
			const isTotalRow = account == null;
			const drillAccount = isDynamicCategory || isTotalRow
				? null
				: (level === 3 ? parent : account);
			const drillSelectedItems = isDynamicCategory && !isTotalRow
				? { [sub]: [account] }
				: {};
			const monthDate = moment(month, "YYYY-MM").toDate();
			// Capitalizado al modo del chip del diseño ("Abr 2025") — fallback
			// para el header si endMonth no se renderea (el caller ya pasa
			// endMonth, así que en práctica el AnualCalendar lo reemplaza).
			const periodLabel = moment(month, "YYYY-MM")
				.locale("es")
				.format("MMM YYYY")
				.replace(/^./, (c) => c.toUpperCase());

			this.fetchSalesAnalysis({
				month,
				account: drillAccount,
				groupBy: this.analysisGroupByForOpen,
				selectedItems: drillSelectedItems,
			});
			this.$buefy.modal.open({
				parent: this,
				hasModalCard: true,
				customClass: "wide-summary-modal",
				canCancel: ["escape", "outside"],
				component: ModalFullHeightRight,
				props: {
					headerComp: HeaderSalesAnalysis,
					bodyComp: BodySalesAnalysis,
					headerProps: {
						periodLabel,
						endMonth: monthDate,
						getNow: this.getNow,
						categories: this.headerCategories,
					},
					bodyProps: null,
					// El modal se monta en document.body — sin esto el subárbol
					// no hereda el [data-theme="ventas"] del .ventas_tab.
					theme: "ventas",
				},
				events: {
					closed: this.onAnalysisClose,
					"update-end-month": this.onAnalysisMonthChange,
					"category-change": this.onCategoryChange,
				},
			});
		},
	},

	mounted() {
		// Bootstrap orquestado por la action — el view declara intent ("init
		// del tab Ventas") y el store decide qué se carga en paralelo. Los
		// watchers de fetchSignature/summarySignature siguen llamando las
		// actions individuales según corresponda.
		this.initVentas();
		// classification/getCategories alimenta `categories` + `itemsByCategory`
		// que `subNavFilters` y `headerCategories` consumen. Si el usuario
		// llegó a Ventas por otra ruta (DashboardClassificator ya las cargó),
		// la action hace short-circuit.
		this.getCategories();
	},

	beforeDestroy() {
		// Si el tab se desmonta con un fetch debounced pendiente, lo cancelamos
		// para evitar que dispare contra un store/component ya fuera de scope.
		clearTimeout(this.categoryChangeTimer);
	},

	watch: {
		fetchSignature() { this.fetchSalesData(); },
		summarySignature() { this.fetchSalesSummary(); },
	},
};
</script>

<style scoped lang="scss">
@import "@/assets/style.scss";

.ventas_tab {
	position: relative;
	height: 100%;
}

// El header común vive en TabHeader.vue. Acá solo queda el SCSS de los
// sub-filters dinámicos (Cuentas/Categorías/Clientes) inyectados vía
// el slot #center-sub-nav — re-usan las classes .sub_nav_filters/.filter_btn
// que TabHeader expone con scoped styles propios, así que los repetimos
// localmente para que apliquen al markup del slot.

.sub_nav_filters {
	display: flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
}

.filter_btn {
	padding: 6px 16px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.2s;

	&:hover {
		background: var(--report-surface);
	}

	&.is-active {
		background: var(--report-primary);
		color: var(--report-bg);
	}
}
</style>
